import moment from 'moment';

/**
 * Due-date semantics of to-dos, same as the backend helper in shrd (utils/helpers/toDoDue.helper.ts):
 * with hasDueTime the to-do is due at dueDate, otherwise it is due for the whole day and only overdue once the day is over.
 */
export interface ToDoDueLike {
  dueDate?: Date | string | null;
  hasDueTime?: boolean | null;
  completed?: boolean;
}

export type ToDoDueState = 'none' | 'overdue' | 'today' | 'soon' | 'later';

/** Moment from which the to-do counts as overdue */
export function toDoDeadline(item: ToDoDueLike): moment.Moment | null {
  if (!item.dueDate) {
    return null;
  }
  return item.hasDueTime ? moment(item.dueDate) : moment(item.dueDate).startOf('day').add(1, 'day');
}

export function isToDoOverdue(item: ToDoDueLike, now: moment.Moment = moment()): boolean {
  const deadline = toDoDeadline(item);
  return !!deadline && !item.completed && !deadline.isAfter(now);
}

/** Due today or earlier and not done */
export function isToDoDue(item: ToDoDueLike, now: moment.Moment = moment()): boolean {
  return !!item.dueDate && !item.completed && moment(item.dueDate).isSameOrBefore(now.clone().endOf('day'));
}

export function toDoDueState(item: ToDoDueLike, now: moment.Moment = moment()): ToDoDueState {
  if (!item.dueDate || item.completed) {
    return 'none';
  }
  if (isToDoOverdue(item, now)) {
    return 'overdue';
  }
  if (moment(item.dueDate).isSame(now, 'day')) {
    return 'today';
  }
  return moment(item.dueDate).isSameOrBefore(now.clone().add(7, 'days').endOf('day')) ? 'soon' : 'later';
}

/** Tone class of modern.css for the due state */
export function toDoDueTone(item: ToDoDueLike, now: moment.Moment = moment()): string {
  switch (toDoDueState(item, now)) {
    case 'overdue':
      return 'tone-bad';
    case 'today':
      return 'tone-warn';
    case 'soon':
      return 'tone-info';
    default:
      return 'tone-muted';
  }
}

/** Time part, only when the to-do has one */
export function formatToDoTime(item: ToDoDueLike): string {
  return item.dueDate && item.hasDueTime ? moment(item.dueDate).format('HH:mm') : '';
}

/**
 * Human readable due date: "heute", "morgen 14:30", "Mo 12.10.", "12.10.2027 09:00"
 * @param style short: without weekday/year when close; long: always full date
 */
export function formatToDoDue(item: ToDoDueLike, style: 'short' | 'long' = 'short', now: moment.Moment = moment()): string {
  if (!item.dueDate) {
    return '';
  }
  const due = moment(item.dueDate);
  const time = formatToDoTime(item);
  const withTime = (text: string) => (time ? `${text}, ${time} Uhr` : text);
  if (style === 'long') {
    return withTime(due.format('dd, DD.MM.YYYY'));
  }
  const days = due.clone().startOf('day').diff(now.clone().startOf('day'), 'days');
  if (days === 0) {
    return withTime('heute');
  }
  if (days === 1) {
    return withTime('morgen');
  }
  if (days === -1) {
    return withTime('gestern');
  }
  if (Math.abs(days) < 7) {
    return withTime(due.format('dd, DD.MM.'));
  }
  return withTime(due.format(due.isSame(now, 'year') ? 'DD.MM.' : 'DD.MM.YYYY'));
}

/** Combines a day and an optional "HH:mm" time into the stored due date (local midnight without time) */
export function combineDueDate(day: Date | string | null | undefined, time?: string | null): Date | null {
  if (!day) {
    return null;
  }
  const result = moment(day).startOf('day');
  const match = time ? /^(\d{1,2}):(\d{2})$/.exec(time) : null;
  if (match) {
    result.hours(Number(match[1])).minutes(Number(match[2]));
  }
  return result.toDate();
}

/* ---------- Vorschlag der Fälligkeit beim manuellen Anlegen ---------- */

const isWeekend = (day: moment.Moment) => day.isoWeekday() >= 6;

/**
 * Tag (00:00) `workdays` Werktage nach `from`; Samstag und Sonntag zählen nicht.
 * Feiertage bleiben unberücksichtigt (im Frontend gibt es keinen synchronen Feiertags-Helper).
 * 0 Werktage: `from` selbst bzw. am Wochenende der folgende Montag.
 */
export function addWorkdays(from: Date | moment.Moment, workdays: number): Date {
  const day = moment(from).startOf('day');
  if (workdays <= 0) {
    while (isWeekend(day)) {
      day.add(1, 'day');
    }
    return day.toDate();
  }
  let left = workdays;
  while (left > 0) {
    day.add(1, 'day');
    if (!isWeekend(day)) {
      left--;
    }
  }
  return day.toDate();
}

/** Montag (00:00) der nächsten Kalenderwoche */
export function nextWeekMonday(from: Date | moment.Moment = moment()): Date {
  return moment(from).startOf('isoWeek').add(1, 'week').toDate();
}

/** Werktage des Vorschlags beim manuellen Anlegen eines To-dos */
export const SUGGESTED_DUE_WORKDAYS = 3;

/** Vorbelegte Fälligkeit eines manuell angelegten To-dos: in 3 Werktagen (ohne Uhrzeit) */
export function suggestedToDoDueDate(now: Date | moment.Moment = moment()): Date {
  return addWorkdays(now, SUGGESTED_DUE_WORKDAYS);
}

export type DueQuickChoice = { key: string; text: string; date: (now: moment.Moment) => Date | null };

/** Schnellauswahl beim Anlegen: Heute, Morgen, in 3 Werktagen, nächste Woche, ohne Termin */
export const WORKDAY_DUE_CHOICES: DueQuickChoice[] = [
  {key: 'today', text: 'Heute', date: (now) => now.clone().startOf('day').toDate()},
  {key: 'tomorrow', text: 'Morgen', date: (now) => now.clone().startOf('day').add(1, 'day').toDate()},
  {key: 'workdays3', text: 'In 3 Werktagen', date: (now) => addWorkdays(now, SUGGESTED_DUE_WORKDAYS)},
  {key: 'nextWeek', text: 'Nächste Woche', date: (now) => nextWeekMonday(now)},
  {key: 'none', text: 'Ohne Termin', date: () => null},
];

/** Schlüssel der Schnellauswahl, die zum Tag passt (zuerst die genauere, z. B. „In 3 Werktagen“ vor „Nächste Woche“) */
export function activeDueChoice(day: Date | string | null | undefined, now: moment.Moment = moment()): string | null {
  const match = WORKDAY_DUE_CHOICES.find((choice) => {
    const date = choice.date(now);
    return day ? !!date && moment(date).isSame(day, 'day') : date === null;
  });
  return match ? match.key : null;
}

/* ---------- Alter offener To-dos (Altlasten finden) ---------- */

/** Ganze Tage seit dem Anlegen */
export function toDoAgeDays(item: { createdAt?: Date | string | null }, now: moment.Moment = moment()): number {
  return item.createdAt ? now.clone().startOf('day').diff(moment(item.createdAt).startOf('day'), 'days') : 0;
}

/** Offen, ohne Termin und älter als `days` Tage */
export function isStaleWithoutDue(item: ToDoDueLike & { createdAt?: Date | string | null }, days = 30, now: moment.Moment = moment()): boolean {
  return !item.dueDate && !item.completed && toDoAgeDays(item, now) > days;
}
